// Density-based clustering (DBSCAN) for point clouds, accelerated with a uniform voxel grid.

export const CLUSTER_PARAMS = {
  lidar: { eps: 0.5, minPoints: 1 },
  // Radar returns are much sparser than LiDAR.
  radar: { eps: 1.0, minPoints: 1 },
};

const UNVISITED = 0;
const NOISE = -1;

/**
 * Finds the cluster with the most points.
 * @param {ArrayLike<number>} positions Flat xyz coordinates.
 * @param {{eps: number, minPoints: number}} params Neighbourhood radius (m) and minimum points to form a core point.
 * @returns {Int32Array|null} Indices of the points in the largest cluster, or null if no cluster exists.
 */
export function findLargestCluster(positions, { eps, minPoints }) {
  const count = Math.floor(positions.length / 3);
  if (count < minPoints) return null;

  const min = [Infinity, Infinity, Infinity];
  for (let i = 0; i < count; i += 1) {
    for (let axis = 0; axis < 3; axis += 1) {
      min[axis] = Math.min(min[axis], positions[i * 3 + axis]);
    }
  }

  const cellIndex = new Int32Array(count * 3);
  const dims = [1, 1, 1];
  for (let i = 0; i < count; i += 1) {
    for (let axis = 0; axis < 3; axis += 1) {
      const cell = Math.floor((positions[i * 3 + axis] - min[axis]) / eps);
      cellIndex[i * 3 + axis] = cell;
      dims[axis] = Math.max(dims[axis], cell + 1);
    }
  }

  const cellKey = (cx, cy, cz) => ((cx * dims[1]) + cy) * dims[2] + cz;
  const grid = new Map();
  for (let i = 0; i < count; i += 1) {
    const key = cellKey(cellIndex[i * 3], cellIndex[i * 3 + 1], cellIndex[i * 3 + 2]);
    const bucket = grid.get(key);
    if (bucket) bucket.push(i); else grid.set(key, [i]);
  }

  const epsSquared = eps * eps;
  function neighbours(index) {
    const result = [];
    const px = positions[index * 3];
    const py = positions[index * 3 + 1];
    const pz = positions[index * 3 + 2];
    const cx = cellIndex[index * 3];
    const cy = cellIndex[index * 3 + 1];
    const cz = cellIndex[index * 3 + 2];
    for (let ox = Math.max(0, cx - 1); ox <= Math.min(dims[0] - 1, cx + 1); ox += 1) {
      for (let oy = Math.max(0, cy - 1); oy <= Math.min(dims[1] - 1, cy + 1); oy += 1) {
        for (let oz = Math.max(0, cz - 1); oz <= Math.min(dims[2] - 1, cz + 1); oz += 1) {
          const bucket = grid.get(cellKey(ox, oy, oz));
          if (bucket) {
            for (const other of bucket) {
              const dx = positions[other * 3] - px;
              const dy = positions[other * 3 + 1] - py;
              const dz = positions[other * 3 + 2] - pz;
              if (dx * dx + dy * dy + dz * dz <= epsSquared) result.push(other);
            }
          }
        }
      }
    }
    return result;
  }

  const labels = new Int32Array(count);
  let clusterId = 0;
  let bestId = 0;
  let bestSize = 0;

  for (let i = 0; i < count; i += 1) {
    if (labels[i] === UNVISITED) {
      const seeds = neighbours(i);
      if (seeds.length < minPoints) {
        labels[i] = NOISE;
      } else {
        clusterId += 1;
        labels[i] = clusterId;
        let size = 1;
        for (let s = 0; s < seeds.length; s += 1) {
          const point = seeds[s];
          if (labels[point] === NOISE) {
            labels[point] = clusterId;
            size += 1;
          } else if (labels[point] === UNVISITED) {
            labels[point] = clusterId;
            size += 1;
            const pointNeighbours = neighbours(point);
            if (pointNeighbours.length >= minPoints) {
              for (const candidate of pointNeighbours) seeds.push(candidate);
            }
          }
        }
        if (size > bestSize) {
          bestSize = size;
          bestId = clusterId;
        }
      }
    }
  }

  if (bestId === 0) return null;
  const result = new Int32Array(bestSize);
  let written = 0;
  for (let i = 0; i < count; i += 1) {
    if (labels[i] === bestId) {
      result[written] = i;
      written += 1;
    }
  }
  return result;
}

/**
 * Axis-aligned bounding box of the indexed points.
 * @param {ArrayLike<number>} positions Flat xyz coordinates.
 * @param {ArrayLike<number>} indices Point indices to enclose.
 * @returns {{center: number[], size: number[]}}
 */
export function computeBoundingBox(positions, indices) {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (let n = 0; n < indices.length; n += 1) {
    for (let axis = 0; axis < 3; axis += 1) {
      const value = positions[indices[n] * 3 + axis];
      if (value < min[axis]) min[axis] = value;
      if (value > max[axis]) max[axis] = value;
    }
  }
  return {
    center: min.map((value, axis) => (value + max[axis]) / 2),
    size: min.map((value, axis) => max[axis] - value),
  };
}
